/**
 * OfflineManager -- Full IndexedDB implementation for AWENI.
 *
 * Uses the `idb` library for Promise-based IndexedDB access.
 * Manages 22 object stores covering all AWENI modules.
 */

import { openDB, type IDBPDatabase } from 'idb';

// ---------------------------------------------------------------------------
// Database schema version & name
// ---------------------------------------------------------------------------

const DB_NAME = 'aweni-offline-v1';
const DB_VERSION = 1;

// ---------------------------------------------------------------------------
// Store names (22 total)
// ---------------------------------------------------------------------------

export const STORES = {
  META: 'meta',
  USER_PROFILE: 'user_profile',
  PENDING_ACTIONS: 'pending_actions',
  GAME_SESSIONS: 'game_sessions',
  QURAN_CHAPTERS: 'quran_chapters',
  QURAN_VERSES: 'quran_verses',
  QURAN_TRANSLATIONS: 'quran_translations',
  MEMORIZ_CARDS: 'memoriz_cards',
  MEMORIZ_REVIEWS: 'memoriz_reviews',
  DECK_CARDS: 'deck_cards',
  ARTICLES: 'articles',
  ARTICLE_INDEX: 'article_index',
  I18N_CACHE: 'i18n_cache',
  GAME_CATALOG: 'game_catalog',
  GAME_ASSETS: 'game_assets',
  BALANCETAB_GROUPS: 'balancetab_groups',
  BALANCETAB_TRANSACTIONS: 'balancetab_transactions',
  SELECTO_VENUES: 'selecto_venues',
  SELECTO_ORDERS: 'selecto_orders',
  COMMUNITY_DATA: 'community_data',
  NOTIFICATION_CACHE: 'notification_cache',
  DOWNLOAD_PACKS: 'download_packs',
} as const;

export type StoreName = (typeof STORES)[keyof typeof STORES];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MetaRecord {
  key: string;
  value: string;
  updatedAt: number;
}

export interface PendingAction {
  id: string;
  idempotency_key: string;
  moduleId: string;
  action: string;
  url: string;
  method: 'POST' | 'PUT' | 'DELETE';
  payload: unknown;
  headers: Record<string, string>;
  status: 'pending' | 'syncing' | 'synced' | 'failed';
  createdAt: number;
  syncedAt: number | null;
  retryCount: number;
  maxRetries: number;
  lastError: string | null;
  serverResponse: unknown | null;
}

export interface CacheEntry<T = unknown> {
  key: string;
  value: T;
  moduleId: string;
  storedAt: number;
  expiresAt: number | null;
}

// ---------------------------------------------------------------------------
// Database schema definition (used in upgrade callback)
// ---------------------------------------------------------------------------

function upgradeDatabase(db: IDBPDatabase, oldVersion: number): void {
  if (oldVersion < 1) {
    // -- meta: key-value store for version tracking, sync timestamps, etc.
    db.createObjectStore(STORES.META, { keyPath: 'key' });

    // -- user_profile: cached user/profile data
    const userProfile = db.createObjectStore(STORES.USER_PROFILE, { keyPath: 'id' });
    userProfile.createIndex('email', 'email', { unique: false });

    // -- pending_actions: offline mutation queue
    const pendingActions = db.createObjectStore(STORES.PENDING_ACTIONS, { keyPath: 'id' });
    pendingActions.createIndex('moduleId', 'moduleId', { unique: false });
    pendingActions.createIndex('status', 'status', { unique: false });
    pendingActions.createIndex('createdAt', 'createdAt', { unique: false });

    // -- game_sessions: cached game session data
    const gameSessions = db.createObjectStore(STORES.GAME_SESSIONS, { keyPath: 'id' });
    gameSessions.createIndex('gameKey', 'gameKey', { unique: false });
    gameSessions.createIndex('profileId', 'profileId', { unique: false });
    gameSessions.createIndex('completedAt', 'completedAt', { unique: false });

    // -- quran_chapters: surah metadata (114 entries)
    db.createObjectStore(STORES.QURAN_CHAPTERS, { keyPath: 'number' });

    // -- quran_verses: ayah text (6236 entries)
    const quranVerses = db.createObjectStore(STORES.QURAN_VERSES, { keyPath: 'id' });
    quranVerses.createIndex('surah', 'surah', { unique: false });
    quranVerses.createIndex('surah_ayah', ['surah', 'ayah'], { unique: true });

    // -- quran_translations: translated ayah text
    const quranTranslations = db.createObjectStore(STORES.QURAN_TRANSLATIONS, {
      keyPath: 'id',
    });
    quranTranslations.createIndex('surah', 'surah', { unique: false });
    quranTranslations.createIndex('lang', 'lang', { unique: false });

    // -- memoriz_cards: spaced repetition cards
    const memorizCards = db.createObjectStore(STORES.MEMORIZ_CARDS, { keyPath: 'id' });
    memorizCards.createIndex('surah', 'surah', { unique: false });
    memorizCards.createIndex('due', 'due', { unique: false });
    memorizCards.createIndex('moduleId', 'moduleId', { unique: false });

    // -- memoriz_reviews: review log
    const memorizReviews = db.createObjectStore(STORES.MEMORIZ_REVIEWS, { keyPath: 'id' });
    memorizReviews.createIndex('cardId', 'cardId', { unique: false });
    memorizReviews.createIndex('reviewedAt', 'reviewedAt', { unique: false });

    // -- deck_cards: custom deck cards
    const deckCards = db.createObjectStore(STORES.DECK_CARDS, { keyPath: 'id' });
    deckCards.createIndex('deckId', 'deckId', { unique: false });
    deckCards.createIndex('due', 'due', { unique: false });

    // -- articles: cached blog articles (full content)
    const articles = db.createObjectStore(STORES.ARTICLES, { keyPath: 'slug' });
    articles.createIndex('category', 'category', { unique: false });
    articles.createIndex('date', 'date', { unique: false });

    // -- article_index: lightweight article metadata for search/browse
    const articleIndex = db.createObjectStore(STORES.ARTICLE_INDEX, { keyPath: 'slug' });
    articleIndex.createIndex('category', 'category', { unique: false });

    // -- i18n_cache: cached translation bundles per locale
    db.createObjectStore(STORES.I18N_CACHE, { keyPath: 'locale' });

    // -- game_catalog: game metadata (list of all games)
    const gameCatalog = db.createObjectStore(STORES.GAME_CATALOG, { keyPath: 'key' });
    gameCatalog.createIndex('category', 'category', { unique: false });

    // -- game_assets: downloaded game files (HTML, JS, CSS, images)
    const gameAssets = db.createObjectStore(STORES.GAME_ASSETS, { keyPath: 'url' });
    gameAssets.createIndex('gameKey', 'gameKey', { unique: false });

    // -- balancetab_groups: cached BT groups
    db.createObjectStore(STORES.BALANCETAB_GROUPS, { keyPath: 'id' });

    // -- balancetab_transactions: cached BT transactions
    const btTransactions = db.createObjectStore(STORES.BALANCETAB_TRANSACTIONS, {
      keyPath: 'id',
    });
    btTransactions.createIndex('groupId', 'groupId', { unique: false });
    btTransactions.createIndex('createdAt', 'createdAt', { unique: false });

    // -- selecto_venues: cached SelectO venues
    const selectoVenues = db.createObjectStore(STORES.SELECTO_VENUES, { keyPath: 'id' });
    selectoVenues.createIndex('slug', 'slug', { unique: true });

    // -- selecto_orders: cached SelectO orders
    const selectoOrders = db.createObjectStore(STORES.SELECTO_ORDERS, { keyPath: 'id' });
    selectoOrders.createIndex('venueId', 'venueId', { unique: false });
    selectoOrders.createIndex('status', 'status', { unique: false });

    // -- community_data: leaderboard, challenges, etc.
    db.createObjectStore(STORES.COMMUNITY_DATA, { keyPath: 'key' });

    // -- notification_cache: cached platform notifications
    const notifications = db.createObjectStore(STORES.NOTIFICATION_CACHE, { keyPath: 'id' });
    notifications.createIndex('read', 'read', { unique: false });
    notifications.createIndex('createdAt', 'createdAt', { unique: false });

    // -- download_packs: metadata about downloaded content packs
    const downloadPacks = db.createObjectStore(STORES.DOWNLOAD_PACKS, { keyPath: 'id' });
    downloadPacks.createIndex('moduleId', 'moduleId', { unique: false });
    downloadPacks.createIndex('status', 'status', { unique: false });
  }
}

// ---------------------------------------------------------------------------
// OfflineManager
// ---------------------------------------------------------------------------

export class OfflineManager {
  private dbPromise: Promise<IDBPDatabase> | null = null;

  // ---- Init ---------------------------------------------------------------

  async init(): Promise<void> {
    await this.getDB();
    this.listenForDataDeltas();
  }

  private getDB(): Promise<IDBPDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDB(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion) {
          upgradeDatabase(db, oldVersion);
        },
        blocked() {
          // Another tab has an older version open — user needs to close it
          console.warn('[OfflineManager] Database upgrade blocked by another tab');
        },
        blocking() {
          // This tab is blocking a newer version — close our connection
          console.warn('[OfflineManager] This tab is blocking a database upgrade');
        },
      });
    }
    return this.dbPromise;
  }

  // ---- Generic CRUD -------------------------------------------------------

  async get<T>(storeName: StoreName, key: IDBValidKey): Promise<T | undefined> {
    const db = await this.getDB();
    return db.get(storeName, key) as Promise<T | undefined>;
  }

  async put<T>(storeName: StoreName, value: T): Promise<IDBValidKey> {
    const db = await this.getDB();
    return db.put(storeName, value as unknown as Record<string, unknown>);
  }

  async getAll<T>(storeName: StoreName): Promise<T[]> {
    const db = await this.getDB();
    return db.getAll(storeName) as Promise<T[]>;
  }

  async getAllFromIndex<T>(
    storeName: StoreName,
    indexName: string,
    query?: IDBValidKey | IDBKeyRange,
  ): Promise<T[]> {
    const db = await this.getDB();
    return db.getAllFromIndex(storeName, indexName, query) as Promise<T[]>;
  }

  async delete(storeName: StoreName, key: IDBValidKey): Promise<void> {
    const db = await this.getDB();
    await db.delete(storeName, key);
  }

  async count(storeName: StoreName, query?: IDBValidKey | IDBKeyRange): Promise<number> {
    const db = await this.getDB();
    return db.count(storeName, query);
  }

  async clear(storeName: StoreName): Promise<void> {
    const db = await this.getDB();
    await db.clear(storeName);
  }

  // ---- Bulk operations ----------------------------------------------------

  async putMany<T>(storeName: StoreName, items: T[]): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    for (const item of items) {
      store.put(item as unknown as Record<string, unknown>);
    }
    await tx.done;
  }

  async deleteMany(storeName: StoreName, keys: IDBValidKey[]): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    for (const key of keys) {
      store.delete(key);
    }
    await tx.done;
  }

  // ---- Meta helpers -------------------------------------------------------

  async getVersion(key: string): Promise<string | null> {
    const record = await this.get<MetaRecord>(STORES.META, key);
    return record?.value ?? null;
  }

  async setVersion(key: string, version: string): Promise<void> {
    await this.put<MetaRecord>(STORES.META, {
      key,
      value: version,
      updatedAt: Date.now(),
    });
  }

  async getLastSync(): Promise<number | null> {
    const record = await this.get<MetaRecord>(STORES.META, 'lastSync');
    return record ? parseInt(record.value, 10) : null;
  }

  async setLastSync(timestamp?: number): Promise<void> {
    await this.put<MetaRecord>(STORES.META, {
      key: 'lastSync',
      value: String(timestamp ?? Date.now()),
      updatedAt: Date.now(),
    });
  }

  // ---- Pending Actions helpers --------------------------------------------

  async addPendingAction(action: PendingAction): Promise<void> {
    await this.put(STORES.PENDING_ACTIONS, action);
  }

  async getPendingActions(): Promise<PendingAction[]> {
    return this.getAllFromIndex<PendingAction>(
      STORES.PENDING_ACTIONS,
      'status',
      'pending',
    );
  }

  async getFailedActions(): Promise<PendingAction[]> {
    return this.getAllFromIndex<PendingAction>(
      STORES.PENDING_ACTIONS,
      'status',
      'failed',
    );
  }

  async updateActionStatus(
    id: string,
    status: PendingAction['status'],
    extra?: Partial<PendingAction>,
  ): Promise<void> {
    const action = await this.get<PendingAction>(STORES.PENDING_ACTIONS, id);
    if (!action) return;
    await this.put(STORES.PENDING_ACTIONS, {
      ...action,
      ...extra,
      status,
      syncedAt: status === 'synced' ? Date.now() : action.syncedAt,
    });
  }

  async getPendingCount(): Promise<number> {
    const db = await this.getDB();
    return db.countFromIndex(STORES.PENDING_ACTIONS, 'status', 'pending');
  }

  // ---- Cleanup ------------------------------------------------------------

  /**
   * Remove synced actions older than 7 days and expired cache entries.
   */
  async cleanup(): Promise<{ removedActions: number; removedCache: number }> {
    await this.getDB();
    let removedActions = 0;
    let removedCache = 0;

    // Clean synced actions older than 7 days
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const actions = await this.getAll<PendingAction>(STORES.PENDING_ACTIONS);
    const toDelete = actions.filter(
      (a) => a.status === 'synced' && a.syncedAt !== null && a.syncedAt < sevenDaysAgo,
    );
    if (toDelete.length > 0) {
      await this.deleteMany(
        STORES.PENDING_ACTIONS,
        toDelete.map((a) => a.id),
      );
      removedActions = toDelete.length;
    }

    // Clean expired notification cache
    const notifications = await this.getAll<{ id: string; expiresAt?: number }>(
      STORES.NOTIFICATION_CACHE,
    );
    const expiredNotifs = notifications.filter(
      (n) => n.expiresAt && n.expiresAt < Date.now(),
    );
    if (expiredNotifs.length > 0) {
      await this.deleteMany(
        STORES.NOTIFICATION_CACHE,
        expiredNotifs.map((n) => n.id),
      );
      removedCache = expiredNotifs.length;
    }

    return { removedActions, removedCache };
  }

  // ---- Storage estimate per store -----------------------------------------

  async getStoreRecordCount(storeName: StoreName): Promise<number> {
    return this.count(storeName);
  }

  async getAllStoreCounts(): Promise<Record<StoreName, number>> {
    const counts: Partial<Record<StoreName, number>> = {};
    for (const name of Object.values(STORES)) {
      try {
        counts[name] = await this.count(name);
      } catch {
        counts[name] = 0;
      }
    }
    return counts as Record<StoreName, number>;
  }

  // ---- Data delta listener (from SW/UpdateManager) ------------------------

  private listenForDataDeltas(): void {
    try {
      const bc = new BroadcastChannel('aweni-updates');
      bc.addEventListener('message', async (event: MessageEvent) => {
        if (event.data?.type === 'data-delta') {
          await this.applyDataDelta(event.data);
        }
      });
    } catch {
      // BroadcastChannel not supported — deltas applied via direct call
    }
  }

  private async applyDataDelta(delta: {
    key: string;
    version: string;
    records: Array<{ action: 'put' | 'delete'; store: StoreName; data: unknown }>;
  }): Promise<void> {
    const db = await this.getDB();

    // Group records by store for batched transactions
    const byStore = new Map<StoreName, Array<{ action: 'put' | 'delete'; data: unknown }>>();
    for (const record of delta.records) {
      if (!byStore.has(record.store)) byStore.set(record.store, []);
      byStore.get(record.store)!.push(record);
    }

    for (const [storeName, records] of byStore) {
      if (!db.objectStoreNames.contains(storeName)) continue;
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      for (const record of records) {
        if (record.action === 'put') {
          store.put(record.data as Record<string, unknown>);
        } else if (record.action === 'delete' && record.data) {
          store.delete(record.data as IDBValidKey);
        }
      }
      await tx.done;
    }

    // Update version in meta
    await this.setVersion(`data_${delta.key}`, delta.version);
  }

  // ---- Close --------------------------------------------------------------

  async close(): Promise<void> {
    if (this.dbPromise) {
      const db = await this.dbPromise;
      db.close();
      this.dbPromise = null;
    }
  }
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------

export const offlineManager = new OfflineManager();
